import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import axios from "axios";
import { authenticator } from "../Authentication/auth-funcs";
import {
  getAuthHeaders,
  buildAuthHeaders,
  FORM_URLENCODED_CONTENT_TYPE,
} from "../backendIntegration/authHeaders";
import { addCard } from "./appliedJobs";
import { pollUserProfile } from "./profileData";
import {
  INTERCHANGEABLE_LINKS,
  isBlocking,
  isProfileBacked,
  questionIdentity,
} from "../utils/applicationQuestions";
import logger from '../utils/logger';
import { postedWithinHours } from "../utils/postedWithin";
import { isJobsUnavailable, isRateLimited, retryAfterMs } from "../backendIntegration/apiErrors";


/**
 * Fetches new job cards from the backend.
 * It can operate in two modes:
 * 1. Fresh load (replaces the deck): When `exclude` is empty.
 * 2. Pre-fetching (appends to the deck): When `exclude` has URLs, it tells the
 *    backend to not send those specific jobs, avoiding duplicates from the race condition.
 */
export const fetchJobCards = createAsyncThunk(
  "jobs/fetchJobCards",
  async ({ filters = {}, exclude = [] }, { rejectWithValue, signal, getState }) => {
    // Read before the request goes out: `pending` has already bumped this for a fresh
    // load, so a response tagged with an older generation was fetched under filters the
    // user has since replaced. Mirrors iOS's JobCardsViewModel.fetchGeneration.
    const generation = getState().jobCards.fetchGeneration;
    // A fresh load (filters, retry) replaces the deck, so a refill waiting on its backoff is moot.
    if (exclude.length === 0) cancelScheduledRefill();
    try {
      const user = await authenticator.getCurrentUser();
      const headers = await getAuthHeaders({
        contentType: FORM_URLENCODED_CONTENT_TYPE,
        includeIdentityId: false,
      });

      const queryParams = new URLSearchParams();

      if (filters.fromDB) {
        queryParams.append("fromDB", "true");
      } else {
        if (filters.jobIndustry) {
          queryParams.append("jobIndustry", filters.jobIndustry);
        }
        if (filters.location) {
          // if (filters.location.city) queryParams.append("locationCity", filters.location.city);
          if (filters.location.state) queryParams.append("locationState", filters.location.state);
          // if (filters.location.country) queryParams.append("locationCountry", filters.location.country);
          // if (filters.location.isRemote !== undefined) queryParams.append("isRemote", filters.location.isRemote.toString());
        }
        if (filters.jobType && filters.jobType.length > 0) {
          queryParams.append("jobType", filters.jobType.join(","));
        }
        if (filters.companyKey) {
          if (filters.companyKey.id) queryParams.append("companyId", filters.companyKey.id);
          if (filters.companyKey.name) queryParams.append("companyName", filters.companyKey.name);
        }
        // Snapped to a preset we still offer, so a profile holding a retired window (3h, 14d)
        // queries the same window its chip is showing rather than a third, invisible one.
        const withinHours = postedWithinHours(filters);
        if (withinHours) {
          queryParams.append("postedWithinHours", withinHours.toString());
          // Cutoff computed per request so the window never ages; a legacy absolute cutoff
          // resolves to a window above, so it is never sent raw.
          queryParams.append("postedAfterTimestamp", (Date.now() - withinHours * 3_600_000).toString());
        }
        if (filters.minimumSalary) {
          queryParams.append("minimumSalary", filters.minimumSalary.toString());
        }
      }

      if (exclude && exclude.length > 0) {
        queryParams.append("exclude", exclude.join(","));
      }

      const url = `${process.env.REACT_APP_API_DOMAIN}/users/${user.userId}/jobs?${queryParams.toString()}`;
      const response = await axios.get(url, { headers, signal });

      return {
        newJobs: response.data.data,
        // Why the deck is what it is: 'ok' | 'exhausted' | 'profileIncomplete'. Only
        // 'exhausted' means "you've seen everything"; an outage never gets here — the
        // server answers 503 MATCHING_UNAVAILABLE and this thunk rejects.
        deckStatus: response.data.deckStatus ?? null,
        missingProfileFields: response.data.missingProfileFields ?? [],
        isAppending: exclude.length > 0,
        generation,
        answeredAt: Date.now(),
      };
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ aborted: true });
      }
      // The meta lets the reducer drop a failure from a superseded load and count the backoff
      // from when this one failed, not from when it started.
      return rejectWithValue(
        error.response ? error.response.data : "An unknown error in fetchJobCards occurred: " + error,
        { generation, failedAt: Date.now() }
      );
    }
  },
  // When the request went out, for the refill backoff below. Taken here rather than in the
  // reducer so the reducer stays a pure function of its actions.
  { getPendingMeta: () => ({ startedAt: Date.now() }) }
);

/**
 * How long a refill waits after `streak` refills in a row came back with nothing new (or
 * failed): 2 s, doubling, capped at 60 s. Without it, a deck the server has run out of asked
 * /jobs again on every swipe of its last five cards, and a failing backend got the same.
 */
export const REFILL_BACKOFF_BASE_MS = 2_000;
export const REFILL_BACKOFF_MAX_MS = 60_000;
export const refillBackoffMs = (streak) =>
  streak > 0 ? Math.min(REFILL_BACKOFF_MAX_MS, REFILL_BACKOFF_BASE_MS * 2 ** (streak - 1)) : 0;

const NO_BACKOFF = { emptyStreak: 0, retryAt: 0, lastAttemptAt: 0, failed: false };

// Counted from when the answer landed or the request failed (a slow answer must not use up its
// own wait), else from when the request went out. `minWaitMs` is the server's retryAfterSeconds.
const backOff = (state, answeredAt, { minWaitMs = 0, failed = false } = {}) => {
  const refill = state.refill || NO_BACKOFF;
  const emptyStreak = refill.emptyStreak + 1;
  const from = answeredAt || refill.lastAttemptAt;
  state.refill = {
    ...refill,
    emptyStreak,
    retryAt: from + Math.max(refillBackoffMs(emptyStreak), minWaitMs),
    failed,
  };
};

// The one pending refill-at-retryAt (see handleSwipe). Module-level so a new schedule, a fresh
// load or a filter change replaces it instead of stacking another.
let refillTimer = null;
export const cancelScheduledRefill = () => {
  clearTimeout(refillTimer);
  refillTimer = null;
};

/**
 * What this swipe settles for the rest of the deck, split by *why* it is settled.
 * The two halves are not interchangeable:
 *
 *  - `banked` — the backend wrote the answer to the profile, so no job needs to
 *    ask again. Only `savedToProfile` questions qualify; per-company ones
 *    (relative-of-employee) are stamped false because they legitimately recur per
 *    employer, and a card cached before the flag shipped has it undefined, which
 *    fails safe as "ask again".
 *  - `dismissed` — the user said "don't ask me this". That silences the question
 *    only while employers leave it optional. The backend re-asks the moment a job
 *    requires it (`q.blocking || !dismissed.has(...)` in applicationQuestions.ts),
 *    because "stop asking for my website" cannot mean "submit this application
 *    without the website the employer requires".
 *
 * Keyed by `questionIdentity`, not raw `classification`: every vouch question
 * ("Have you used Terraform?" / "...Kubernetes?" / ...) shares the classification
 * `vouchSkill`, so classification alone can no longer tell two questions apart —
 * banking one skill would otherwise also bank, and hide, every other skill's vouch
 * question on the rest of the deck. See `questionIdentity`.
 *
 * Each question is settled by its own answer. A card can hold several questions of one
 * classification (each "other" question, told apart by label), so an answer is matched to
 * its question by label, the way the worker matches it, and an answer to a sibling never
 * counts as an answer to a question the user left blank. Only an answer that names no
 * question on the card (the older shape, which carries no label) is read by classification.
 *
 * Mirrors iOS's JobCardsViewModel.resolvedClassifications.
 */
export const resolvedClassifications = (jobData, manualUserAnswers, dismissedQuestions) => {
  const fields = jobData?.manualInputFields || [];
  const answers = (manualUserAnswers || []).filter(a => a?.classification);
  const labelsOnCard = new Set(fields.map(f => f?.label).filter(Boolean));
  const answerTo = field =>
    (field.label ? answers.find(a => a.label === field.label) : undefined) ??
    answers.find(a => !labelsOnCard.has(a.label) && questionIdentity(a) === questionIdentity(field));
  const isAnswered = field => String(answerTo(field)?.value ?? "").trim() !== "";
  const banked = new Set();
  for (const field of fields) {
    if (!isProfileBacked(field)) continue;
    const identity = questionIdentity(field);
    if (!identity || !isAnswered(field)) continue;
    banked.add(identity);
    if (INTERCHANGEABLE_LINKS.includes(field.classification)) {
      INTERCHANGEABLE_LINKS.forEach(link => banked.add(link));
    }
  }
  return {
    banked: [...banked],
    dismissed: [...new Set((dismissedQuestions || []).filter(Boolean))],
  };
};

const hasAnything = resolved => Boolean(resolved.banked.length || resolved.dismissed.length);

// Fires one refill when a backoff ends, for a deck that is still short. Dropped if the deck was
// reloaded meanwhile (generation moved) or something else already refilled it.
const scheduleRefill = ({ dispatch, getState, retryAt, seen, threshold }) => {
  cancelScheduledRefill();
  const generation = getState().jobCards.fetchGeneration;
  refillTimer = setTimeout(() => {
    refillTimer = null;
    const { data, isPrefetching, fetchGeneration } = getState().jobCards;
    if (fetchGeneration !== generation || isPrefetching || !Array.isArray(data) || data.length > threshold) return;
    const exclude = [...new Set([...seen, ...data.map((job) => job.jobUrl)])];
    const profileData = getState().profileData.data;
    dispatch(fetchJobCards({ filters: profileData?.filters ? { ...profileData.filters } : {}, exclude }));
  }, Math.max(0, retryAt - Date.now()));
};

export const handleSwipe = createAsyncThunk(
  "jobs/handleSwipe",
  async (
    { jobData, isAccepting, manualUserAnswers, dismissedQuestions, applyAnyway, fitVerdict },
    { getState, dispatch, rejectWithValue }
  ) => {
    const jobUrl = jobData.jobUrl;
    // Resolve up front, not on the response. The card animates off screen in ~1s
    // and the next card becomes swipeable then, while /handleSwipe can take
    // several seconds on a cold start — waiting for it would leave a window where
    // the very next card asks the question the user just answered. Rolled back
    // below if the request turns out never to have reached the persistence step.
    const resolved = resolvedClassifications(jobData, manualUserAnswers, dismissedQuestions);
    if (hasAnything(resolved)) dispatch(jobCardSlice.actions.resolveQuestions(resolved));
    try {
      // 2. PRE-FETCHING LOGIC: Fetch more cards when the deck is low.
      const { data: remainingJobs, isPrefetching, refill } = getState().jobCards; // Get state
            // A cold deck fetch takes seconds: start it with five cards of runway, not two.
      const PREFETCH_THRESHOLD = 5;

      // Calculate how many jobs will remain after this swipe
      const jobsAfterSwipe = remainingJobs.length - 1;

      // --- FIX: Check if we are already pre-fetching ---
      // Trigger pre-fetch if we'll have <= threshold cards after this swipe
      // The last refills came back empty or failed: wait out the backoff instead of asking
      // again on every swipe. A filter change or a retry is a fresh load and clears it.
      // Never on the last card: with nothing left to swipe, a skipped refill would strand the user
      // on an empty deck with no request in flight. Instead one refill is scheduled at retryAt.
      const backingOff = Date.now() < (refill?.retryAt || 0);
      if (jobsAfterSwipe <= PREFETCH_THRESHOLD && !isPrefetching && backingOff && jobsAfterSwipe > 0) {
        logger.log("Pre-fetch skipped: the last refill came back empty, backing off.");
        scheduleRefill({ dispatch, getState, retryAt: refill.retryAt, seen: [jobUrl], threshold: PREFETCH_THRESHOLD });
      } else if (jobsAfterSwipe <= PREFETCH_THRESHOLD && !isPrefetching) {
        logger.log(`Deck will have ${jobsAfterSwipe} cards after swipe. Initiating pre-fetch.`);

        const urlsToExclude = [jobUrl, ...remainingJobs.map(job => job.jobUrl)];
        // Ensure we preserve all current filters, including premium filters
        const profileData = getState().profileData.data;
        const currentFilters = profileData?.filters ? { ...profileData.filters } : {};
        dispatch(fetchJobCards({ filters: currentFilters, exclude: urlsToExclude }));
      } else if (isPrefetching) {
        logger.log("Pre-fetch attempt skipped: Another pre-fetch is already in progress.");
      }

      // Get fresh session details to ensure token is valid
      const [user, session] = await Promise.all([
        authenticator.getCurrentUser(),
        authenticator.getUserSessionDetails()
      ]);

      if (!session || !session.tokens || !session.tokens.idToken) {
        throw new Error("Invalid session or missing authentication token");
      }

      const headers = buildAuthHeaders(session, { includeIdentityId: false });

      const requestBody = {
        jobUrl,
        isAccepting,
        identityId: session.identityId,
        userId: user.userId, // Add user ID for additional validation
        manualUserAnswers: manualUserAnswers || [],
        // Classifications the user pressed "Don't ask this again" on.
        dismissedQuestions: dismissedQuestions || [],
      };
      // Travels whenever the card carried a fit verdict, on accept or discard alike —
      // the backend's outcome record wants `fitVerdict` regardless of swipe direction.
      if (fitVerdict) requestBody.fitVerdict = fitVerdict;
      // Only ever true when the user confirmed on the blocked-fit pre-swipe sheet on
      // an accept; never sent at all on a discard, even if a stray true were passed in.
      if (applyAnyway === true && isAccepting) requestBody.applyAnyway = true;

      const response = await axios.post(
        `${process.env.REACT_APP_API_DOMAIN}/handleSwipe`,
        JSON.stringify(requestBody),
        { headers }
      );

      if (isAccepting) {
        dispatch(addCard(jobData));
      }
      // Refresh whenever the backend banked something, on accept or discard: the
      // profile page reads straight off this slice, so the value the user just typed
      // shows up there without a reload. A plain accept no longer refetches: the
      // only thing it changed that the deck shows is the swipe count, and the
      // response carries that (profileData keeps it as `swipeCounts`). That was a
      // full profile read per apply. `pollUserProfile` rather than
      // `silentFetchUserProfile` because the latter's rejection sets
      // profileData.error, which ProtectedRoute reads as "profile incomplete" and
      // bounces the user to /preferences-quiz — one flaky GET should not throw
      // someone out of the deck mid-swipe.
      if (hasAnything(resolved)) {
        dispatch(pollUserProfile());
      }

      return { jobUrl, isAccepting, resolved, response: response.data };
    } catch (error) {
      logger.error("Error in handleSwipe:", error);
      // The handler persists answers and dismissals before the swipe gate, so a
      // "no swipes left" 403 still banked them and the optimistic resolve stands.
      // Match on the message, not the status alone: API Gateway (WAF, an unmatched
      // route) also answers 403 without the Lambda ever running. Anything else —
      // 401/404/500, or a request that never left the device — means the write may
      // not have happened, so put the questions back.
      const persisted = error.response?.status === 403 &&
        String(error.response?.data?.message || "").includes("No swipes left");
      if (hasAnything(resolved)) {
        if (persisted) dispatch(pollUserProfile());
        else dispatch(jobCardSlice.actions.unresolveQuestions(resolved));
      }
      // Job DB outage: nothing was written and no swipe was spent. Keep the server's words for
      // the snackbar; the reducer puts the card back.
      if (error.response?.status === 503 && isJobsUnavailable(error.response.data)) {
        return rejectWithValue(error.response.data);
      }
      // Handle specific error cases
      if (error.response && error.response.status === 403) {
        // Check if this is a "no more swipes" error
        if (error.response.data && error.response.data.message &&
          error.response.data.message.includes("No swipes left today")) {
          return rejectWithValue({
            message: "No swipes left today.",
            originalError: error.response.data
          });
        }
        return rejectWithValue({
          message: "You don't have permission to perform this action. Please check your account status.",
          originalError: error.response.data
        });
      }
      return rejectWithValue(error.response ? error.response.data : "An unknown error occurred: " + error);
    }
  }
);

const NOTHING_RESOLVED = { banked: [], dismissed: [] };

const union = (existing, incoming) => [...new Set([...(existing || []), ...(incoming || [])])];
const without = (existing, removed) => {
  const drop = new Set(removed || []);
  return (existing || []).filter(c => !drop.has(c));
};

export const jobCardSlice = createSlice({
  name: "jobCards",
  initialState: {
    data: [],
    swipe: null,
    loading: false, // For full, initial loads
    isPrefetching: false, // --- FIX: New state to track background fetches ---
    loaded: false,
    error: null,
    // Server-reported reason for the deck's contents (see fetchJobCards). null until the
    // first fresh load lands, or when talking to a backend that predates it.
    deckStatus: null,
    missingProfileFields: [],
    // Bumped on every fresh load. A response carrying an older value was fired under
    // filters the user has since changed, and is dropped instead of landing in the deck.
    fetchGeneration: 0,
    // What the user has settled this session. The deck is never rewritten — cards
    // keep the questions /jobs stamped on them and `selectVisibleJobCards` filters
    // at read time, which makes resolving instant and undoable.
    resolvedQuestions: NOTHING_RESOLVED,
    // Refill backoff (see refillBackoffMs): how many refills in a row brought nothing,
    // when the next one may go out, and when the last request started.
    refill: NO_BACKOFF,
  },
  reducers: {
    clearJobCards: (state) => {
      state.data = null;
      state.error = null;
      state.loading = false;
      state.isPrefetching = false; // Reset on clear
      state.loaded = false;
      state.deckStatus = null;
      state.missingProfileFields = [];
      // User-scoped: this slice is cleared on sign-out.
      state.resolvedQuestions = NOTHING_RESOLVED;
      state.refill = NO_BACKOFF;
      state.fetchGeneration += 1;
    },
    resolveQuestions: (state, action) => {
      const { banked, dismissed } = action.payload || NOTHING_RESOLVED;
      state.resolvedQuestions = {
        banked: union(state.resolvedQuestions?.banked, banked),
        dismissed: union(state.resolvedQuestions?.dismissed, dismissed),
      };
    },
    // Undo an optimistic resolve whose swipe never reached the profile write.
    unresolveQuestions: (state, action) => {
      const { banked, dismissed } = action.payload || NOTHING_RESOLVED;
      state.resolvedQuestions = {
        banked: without(state.resolvedQuestions?.banked, banked),
        dismissed: without(state.resolvedQuestions?.dismissed, dismissed),
      };
    },
    setSwipe: (state, action) => {
      if (action.payload === "left" && action.payload === "right") {
        throw new Error("Invalid swipe action");
      }
      state.swipe = action.payload;
    },
    updateJobCards: (state, action) => {
      state.data = state.data.filter((job) => job.jobUrl !== action.payload);
      state.swipe = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobCards.pending, (state, action) => {
        // --- FIX: Differentiate between full load and pre-fetch ---
        const isPrefetch = action.meta.arg.exclude?.length > 0;

        if (isPrefetch) {
          state.isPrefetching = true;
        } else {
          // This is a full query (exclude is empty), so show main loader.
          state.fetchGeneration += 1;
          state.loading = true;
        }
        state.error = null;
        state.refill = {
          ...(state.refill || NO_BACKOFF),
          lastAttemptAt: action.meta.startedAt || 0,
          ...(isPrefetch ? {} : { failed: false }),
        };
      })
      .addCase(fetchJobCards.fulfilled, (state, action) => {
        const { newJobs, isAppending, generation, deckStatus, missingProfileFields, answeredAt } = action.payload;
        // Superseded — these cards were fetched under filters the user has replaced.
        // Dropping them is what stops a prefetch fired before the filter changed from
        // appending off-filter jobs onto the deck the new filters just produced.
        // `loading` is left alone: it belongs to the fresh load that superseded this.
        if (generation !== state.fetchGeneration) {
          state.isPrefetching = false;
          return;
        }
        state.loading = false;
        state.isPrefetching = false; // --- FIX: Reset on completion ---
        state.loaded = true;

        if (isAppending) {
          // A prefetch is kicked off before the swipe request that banks the
          // answer, so an appended card can still carry a question the user just
          // settled — `resolvedQuestions` keeps covering it.
          const existingUrls = new Set((state.data || []).map(job => job.jobUrl));
          const uniqueNewJobs = newJobs.filter(job => !existingUrls.has(job.jobUrl));
          if (state.data) state.data.push(...uniqueNewJobs);
          else state.data = uniqueNewJobs;
          if (uniqueNewJobs.length) state.refill = { ...state.refill, emptyStreak: 0, retryAt: 0, failed: false };
          else backOff(state, answeredAt);
        } else {
          state.data = newJobs;
          state.deckStatus = deckStatus ?? null;
          state.missingProfileFields = missingProfileFields ?? [];
          // A fresh load re-reads the profile server-side, so its stamping is
          // authoritative and the local cache has done its job. Dropping it here
          // is also what keeps a stale resolve from outliving a profile edit made
          // somewhere that doesn't clear the deck.
          state.resolvedQuestions = NOTHING_RESOLVED;
          // A fresh load is the user's own doing (opening the deck, a filter, a retry), so it
          // starts the backoff over, already at one step when the server said there is nothing
          // more to deal: the first refill after it would only hear that again.
          state.refill = { ...state.refill, emptyStreak: 0, retryAt: 0, failed: false };
          if (deckStatus === "exhausted") backOff(state, answeredAt);
        }
      })
      .addCase(fetchJobCards.rejected, (state, action) => {
        // Do not update state if the request was aborted. RTK settles an aborted thunk
        // itself, so the payloadCreator's rejectWithValue never lands and `payload` is
        // undefined — `meta.aborted` is the signal that survives.
        if (action.meta.aborted || action.payload?.aborted) {
          return;
        }
        // A failure from a load the user has since replaced (a filter change) says nothing
        // about the current deck: no error, no backoff. Like a late fulfilled, `loading`
        // belongs to the load that superseded it.
        const { generation, failedAt } = action.meta;
        if (generation !== undefined && generation !== state.fetchGeneration) {
          state.isPrefetching = false;
          return;
        }
        state.loading = false;
        state.isPrefetching = false;
        state.loaded = true;
        state.error = action.payload;
        // A failed refill backs off like an empty one: one per swipe would only add load to a
        // backend that is already failing. A failed fresh load leaves it to the retry button.
        if (action.meta.arg?.exclude?.length > 0) {
          backOff(state, failedAt, { minWaitMs: retryAfterMs(action.payload), failed: true });
        }
      })
      .addCase(handleSwipe.fulfilled, (state, action) => {
        // No state changes needed here, handled optimistically
        state.data = state.data.filter(
          (job) => job.jobUrl !== action.payload.jobUrl
        );
      })
      .addCase(handleSwipe.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;

        // Job DB outage or a WAF 429: the swipe was never recorded, so the card comes back unless
        // it is still in the deck (the animation had not removed it yet). The profile slice gives
        // the swipe count back on any rejection. `jobUrl` tells the page which card failed.
        if (isJobsUnavailable(action.payload) || isRateLimited(action.payload)) {
          const { jobData } = action.meta.arg;
          state.error = { ...action.payload, jobUrl: jobData?.jobUrl };
          if (jobData?.jobUrl && Array.isArray(state.data) && !state.data.some((job) => job.jobUrl === jobData.jobUrl)) {
            state.data.unshift(jobData);
          } else if (!Array.isArray(state.data)) {
            state.data = jobData ? [jobData] : [];
          }
          state.swipe = null;
        }

        // If this is a "no swipes left" error, we need to reset the swipe state
        // and make sure the card is not removed from the deck
        if (
          action.payload?.message?.includes("No swipes left today") ||
          action.payload?.originalError?.message?.includes("No swipes left today")
        ) {
          state.swipe = null;
        }
      });
  },
});

/**
 * The deck as the user should see it: every card /jobs handed us, minus the
 * questions this session has already settled.
 *
 * Filtering here rather than rewriting the cards means resolving a question takes
 * effect the instant the user submits the popup — the swipe request can take
 * several seconds to come back, and the next card is swipeable long before that.
 * Cards with nothing to drop are returned by identity, so unaffected cards never
 * re-render.
 */
export const selectVisibleJobCards = createSelector(
  [state => state.jobCards.data, state => state.jobCards.resolvedQuestions],
  (data, resolvedQuestions) => {
    if (!Array.isArray(data)) return data;
    const banked = new Set(resolvedQuestions?.banked || []);
    const dismissed = new Set(resolvedQuestions?.dismissed || []);
    if (!banked.size && !dismissed.size) return data;
    // A dismissal only silences a question the employer left optional — see
    // `resolvedClassifications`. Matched by identity, not raw classification, so a
    // vouch question for one skill never suppresses a sibling vouch question.
    // A banked answer settles the questions of its classification the server saved to the
    // profile, but never one it stamped as not saved (a yes/no offered only as statements, a
    // per-job question that shares the classification): that one still has to be asked. A
    // card cached before the stamp existed has none (undefined), and is settled as before.
    const isSettled = f => {
      const identity = questionIdentity(f);
      return (banked.has(identity) && f.savedToProfile !== false) || (dismissed.has(identity) && !isBlocking(f));
    };
    return data.map(job => {
      const fields = job.manualInputFields;
      if (!fields?.some(isSettled)) return job;
      return { ...job, manualInputFields: fields.filter(f => !isSettled(f)) };
    });
  }
);

// Add this at the end of jobCardSlice.js
export const selectIsEffectivelyLoading = (state) => {
  const { loading, isPrefetching, data, loaded } = state.jobCards;
  // Show loader if it's a "full load" OR if it's a "pre-fetch" and the user has run out of cards.
  return (loading && !loaded) || (isPrefetching && data.length === 0);
};

// New selector for job cards loading only (not filter bar)
export const selectIsJobCardsLoading = (state) => {
  const { loading, data, loaded } = state.jobCards;
  // Only show loading overlay when filtering (full reload) and we already have data loaded
  return loading && loaded && data.length >= 0;
};

export const {
  clearJobCards,
  updateJobCards,
  setSwipe,
  clearError,
  resolveQuestions,
  unresolveQuestions,
} = jobCardSlice.actions;
export default jobCardSlice.reducer;

export const selectDeckStatus = (state) => state.jobCards.deckStatus;
// The last refill failed (as opposed to coming back empty): an empty deck then is not "no
// matches" and needs a way to ask again.
export const selectRefillFailed = (state) => Boolean(state.jobCards.refill?.failed);
export const selectMissingProfileFields = (state) => state.jobCards.missingProfileFields || [];

// The /jobs 503 body: { code: 'MATCHING_UNAVAILABLE', retryable: true, message }. The job DB
// or the embedder is down — a retry is the right offer, and the cards already on screen
// stay (the rejected case never touches state.data).
export const isMatchingUnavailableError = (error) => error?.code === "MATCHING_UNAVAILABLE";
